<template>
    <component
        :is="params.comp"
        :grid="params.grid"
        :column="params.col"
        :displayName="params.displayName"
        v-bind="params.componentProps"
    >
        <!--
                This is the filter dropdown. It is only shown if the column has a filter.
            -->
        <ColumnFilterButton
            v-if="params.col.filterable"
            :grid="params.grid"
            :column="params.col"
        />

        <!--
                This is the sort button. It is only shown if sorting is enabled.
            -->
        <ButtonComp
            class="ag-sort-button position-relative px-2"
            :class="[sortDirection == null ? 'btn-subtle-hover' : '']"
            button-type="Custom"
            @click="setSort"
            v-if="params.enableSorting"
            :icon="sortIcon"
            iconFamily="fa-solid"
        >
            <!-- The default icon is in a slot here so we can add the opacity to it. The other icons are in the button -->
            <IconComp
                v-if="sortDirection == null"
                icon="fa-sort"
                family="fa-solid"
            />
            <sup
                class="small position-absolute top-0 start-0"
                style="margin-left: -3px; margin-top: 7px"
                v-if="sortDirection && sortIndex !== undefined"
            >
                <!-- This is 0 indexed, but we want ot display it as 1 indexed -->
                {{ sortIndex + 1 }}
            </sup>
        </ButtonComp>
    </component>
</template>
<script lang="ts" setup generic="TRowData, TCell = any">
import type { IHeaderParams } from 'ag-grid-community';
import type { IHeaderComponentParams } from '../Classes/params';
import { computed, ref, type Component } from 'vue';
import type { SortDirection } from '@/objects/generated/enums/SortDirection';
import { ButtonComp, IconComp } from '@/components';
import { agSortToSortDirection, arrayToRingIterator, sortDirectionToAgSort } from '@/utilities';
import { useLanguageChanged } from '@/composeables';
import ColumnFilterButton from '../Headers/Filters/ColumnFilterButton.vue';

const props = defineProps<{
    params: IHeaderParams & IHeaderComponentParams<TRowData, TCell> & { comp: Component };
}>();

const sortIndex = ref<number | undefined>(props.params.column.getSortIndex() ?? undefined);

const setSort = (event: MouseEvent) => {
    sortDirection.value = sortingPossibilities.next();

    // If we are holding shift, set the sorting on this column specifically and DONT reset the sorting on other columns.
    // This allows us to sort on serveral columns at once
    if (event.shiftKey) {
        props.params.setSort(sortDirectionToAgSort(sortDirection.value), true);
        return;
    }

    // Happy path. We reset the sort for all columns, applying the current sort for this column
    if (sortDirection.value === null) {
        props.params.grid?.setSortState([]);
    } else {
        props.params.grid?.setSortState([
            {
                columnId: props.params.col.colId,
                dataField: props.params.col.dataField,
                sortDirection: sortDirection.value,
            },
        ]);
    }
};

// If the grid sort changes we need to update the sort index for this column.
// This handles both the case where this column has had its sorting changed,
// and when we remove the sorting from another column, potentially making the sortIndex of this column lower.
props.params.api.addEventListener('sortChanged', () => {
    const column = props.params.api.getColumn(props.params.col.colId);
    if (!column) {
        return;
    }
    sortIndex.value = column.getSortIndex() ?? undefined;
    sortDirection.value = agSortToSortDirection(column.getSort() ?? null);

    sortingPossibilities.setItem(sortDirection.value);
});

// The current sort direction
const sortDirection = ref<SortDirection | null>(agSortToSortDirection(props.params.column.getSort()));

// A list of sorting possibilities
const sortingPossibilities = arrayToRingIterator<SortDirection | null>(['Ascending', 'Descending', null], sortDirection.value);

// The icon to show in the sort button
const sortIcon = computed(() => {
    switch (sortDirection.value) {
        case 'Ascending':
            return 'fa-sort-up';
        case 'Descending':
            return 'fa-sort-down';
        default:
            return undefined;
    }
});

useLanguageChanged(() => props.params.grid?.refreshHeader());
</script>
